/**
 * Календари графика (ПЛ-06): рабочая неделя 5/2 и 6/1, вахта 15/15 и 28/28, одна–три смены,
 * праздники и переносы РК, сезонные ограничения. Одно ядро для сервера и браузера (ПЛ-10).
 *
 * Время в ядре — минуты от 1970-01-01 00:00 по местному времени площадки, без часовых поясов:
 * «2026-10-19T08:00» на сервере и в браузере значит одно и то же.
 */

/** Рабочие промежутки дня: [начало, конец) в минутах от полуночи, по возрастанию, без пересечений */
export type DayIntervals = Array<[number, number]>;

export interface CalendarException {
  /** Первый и последний день (включительно), ГГГГ-ММ-ДД */
  from: string;
  to: string;
  /** true — рабочие дни (перенос, работа в выходной), false — нерабочие */
  work: boolean;
  /** Часы работы для рабочих дней; без них — часы обычного дня недели или первой смены */
  day?: DayIntervals | null;
  note?: string | null;
}

export interface SeasonalRule {
  /** Каждый год, ММ-ДД включительно; «12-01»…«02-28» переходит через год */
  from: string;
  to: string;
  note?: string | null;
}

export interface CalendarDef {
  /** Неделя с понедельника: 7 списков промежутков */
  week: DayIntervals[];
  /** Вахта: дни работы и отдыха по кругу от даты заезда; на днях работы — часы day */
  rotation?: { on: number; off: number; anchor: string; day: DayIntervals } | null;
  /** Праздники РК: kz — выходные по закону с переносами, none — без праздников (вахта, круглосуточно) */
  holidays: 'kz' | 'none';
  exceptions: CalendarException[];
  seasonal: SeasonalRule[];
  /** Часов в рабочем дне — для перевода длительности в дни */
  hoursPerDay: number;
}

const DAY = 1440;
const h = (a: number, b: number): [number, number] => [a * 60, b * 60];

/** Готовые календари (ПЛ-06) */
export const CALENDAR_PRESETS: Array<{ code: string; name: string; def: CalendarDef }> = [
  {
    code: '5x2',
    name: '5/2, одна смена 8 ч',
    def: {
      week: [
        [h(9, 13), h(14, 18)],
        [h(9, 13), h(14, 18)],
        [h(9, 13), h(14, 18)],
        [h(9, 13), h(14, 18)],
        [h(9, 13), h(14, 18)],
        [],
        [],
      ],
      holidays: 'kz',
      exceptions: [],
      seasonal: [],
      hoursPerDay: 8,
    },
  },
  {
    code: '6x1',
    name: '6/1, одна смена 10 ч',
    def: {
      week: [
        [h(8, 13), h(14, 19)],
        [h(8, 13), h(14, 19)],
        [h(8, 13), h(14, 19)],
        [h(8, 13), h(14, 19)],
        [h(8, 13), h(14, 19)],
        [h(8, 13), h(14, 19)],
        [],
      ],
      holidays: 'kz',
      exceptions: [],
      seasonal: [],
      hoursPerDay: 10,
    },
  },
  {
    code: '5x2-2sh',
    name: '5/2, две смены 16 ч',
    def: {
      week: [0, 1, 2, 3, 4].map(() => [h(7, 15), h(15, 23)] as DayIntervals).concat([[], []]),
      holidays: 'kz',
      exceptions: [],
      seasonal: [],
      hoursPerDay: 16,
    },
  },
  {
    code: '7x24',
    name: 'Круглосуточно, три смены',
    def: {
      week: [0, 1, 2, 3, 4, 5, 6].map(() => [h(0, 24)] as DayIntervals),
      holidays: 'none',
      exceptions: [],
      seasonal: [],
      hoursPerDay: 24,
    },
  },
  {
    code: 'vahta15',
    name: 'Вахта 15/15, смена 11 ч',
    def: {
      week: [[], [], [], [], [], [], []],
      rotation: { on: 15, off: 15, anchor: '2026-01-01', day: [h(8, 13), h(14, 20)] },
      holidays: 'none',
      exceptions: [],
      seasonal: [],
      hoursPerDay: 11,
    },
  },
  {
    code: 'vahta28',
    name: 'Вахта 28/28, смена 11 ч',
    def: {
      week: [[], [], [], [], [], [], []],
      rotation: { on: 28, off: 28, anchor: '2026-01-01', day: [h(8, 13), h(14, 20)] },
      holidays: 'none',
      exceptions: [],
      seasonal: [],
      hoursPerDay: 11,
    },
  },
];

/** Календарь 24 часа в сутки без выходных — для задержек «в календарных днях» */
export const CALENDAR_24H: CalendarDef = {
  week: [0, 1, 2, 3, 4, 5, 6].map(() => [[0, DAY]] as DayIntervals),
  holidays: 'none',
  exceptions: [],
  seasonal: [],
  hoursPerDay: 24,
};

/* ───────────────────────── Даты ───────────────────────── */

/** Номер дня от 1970-01-01 */
export function dayNumber(d: string): number {
  const y = Number(d.slice(0, 4));
  const m = Number(d.slice(5, 7));
  const dd = Number(d.slice(8, 10));
  return Math.floor(Date.UTC(y, m - 1, dd) / 86_400_000);
}

export function dayString(n: number): string {
  return new Date(n * 86_400_000).toISOString().slice(0, 10);
}

/** «2026-10-19» или «2026-10-19T08:30» → минуты */
export function parseMoment(s: string): number {
  const day = dayNumber(s);
  const hh = s.length >= 16 ? Number(s.slice(11, 13)) : 0;
  const mm = s.length >= 16 ? Number(s.slice(14, 16)) : 0;
  return day * DAY + hh * 60 + mm;
}

/** Минуты → «2026-10-19T08:30» */
export function formatMoment(t: number): string {
  const day = Math.floor(t / DAY);
  const m = t - day * DAY;
  const hh = String(Math.floor(m / 60)).padStart(2, '0');
  const mm = String(m % 60).padStart(2, '0');
  return `${dayString(day)}T${hh}:${mm}`;
}

/** День недели: 0 — понедельник */
export function weekday(dayNo: number): number {
  return (((dayNo + 3) % 7) + 7) % 7;
}

/* ───────────────────────── Праздники РК ───────────────────────── */

/** Первый день Курбан айта (выходной по закону, на другой день не переносится) */
const KURBAN: Record<number, string> = {
  2024: '2024-06-16',
  2025: '2025-06-06',
  2026: '2026-05-27',
  2027: '2027-05-16',
  2028: '2028-05-05',
  2029: '2029-04-24',
  2030: '2030-04-13',
  2031: '2031-04-02',
  2032: '2032-03-22',
  2033: '2033-03-11',
  2034: '2034-03-01',
  2035: '2035-02-18',
};

/**
 * Переносы рабочих дней постановлениями Правительства РК: { день отдыха: рабочая суббота }.
 * Добавляются по мере выхода постановлений; на 2026 год переносов нет.
 */
export const KZ_TRANSFERS: Record<string, string> = {
  '2025-01-03': '2025-01-05',
};

/**
 * Праздничные нерабочие дни РК за год (Закон «О праздниках в Республике Казахстан»): совпавший
 * с выходным праздник переносится на следующий рабочий день, кроме 7 января и Курбан айта.
 */
export function kzHolidays(year: number): Map<string, string> {
  const out = new Map<string, string>();
  const fixed: Array<[string, string, boolean]> = [
    ['01-01', 'Новый год', true],
    ['01-02', 'Новый год', true],
    ['01-07', 'Рождество', false],
    ['03-08', 'Международный женский день', true],
    ['03-21', 'Наурыз мейрамы', true],
    ['03-22', 'Наурыз мейрамы', true],
    ['03-23', 'Наурыз мейрамы', true],
    ['05-01', 'Праздник единства народа Казахстана', true],
    ['05-07', 'День защитника Отечества', true],
    ['05-09', 'День Победы', true],
    ['07-06', 'День Столицы', true],
    ['10-25', 'День Республики', true],
    ['12-16', 'День Независимости', true],
  ];
  // День Конституции: 30 августа до 2025 года, с 2027 года — 15 марта (в 2026 году не отмечается)
  if (year <= 2025) fixed.push(['08-30', 'День Конституции', true]);
  if (year >= 2027) fixed.push(['03-15', 'День Конституции', true]);
  const moved: Array<[number, string]> = [];
  for (const [md, name, movable] of fixed) {
    const d = dayNumber(`${year}-${md}`);
    out.set(dayString(d), name);
    if (movable && weekday(d) >= 5) moved.push([d, name]);
  }
  const k = KURBAN[year];
  if (k) out.set(k, 'Курбан айт');
  // Перенос с выходного: на ближайший следующий рабочий день, не занятый другим праздником
  for (const [d, name] of moved.sort((a, b) => a[0] - b[0])) {
    let n = d + 1;
    while (weekday(n) >= 5 || out.has(dayString(n))) n++;
    out.set(dayString(n), `${name} (перенос)`);
  }
  for (const [off] of Object.entries(KZ_TRANSFERS)) {
    if (off.startsWith(String(year))) out.set(off, 'Перенос выходного дня');
  }
  return out;
}

/** Рабочие субботы по переносам */
function kzWorkingSaturdays(year: number): Set<string> {
  return new Set(Object.values(KZ_TRANSFERS).filter((d) => d.startsWith(String(year))));
}

/* ───────────────────────── Скомпилированный календарь ───────────────────────── */

/**
 * Календарь, развёрнутый по дням на горизонт расчёта: у каждого дня — рабочие промежутки и сумма
 * рабочих минут до его начала. Перевод момента в рабочие минуты и обратно — двоичным поиском.
 */
export class WorkCalendar {
  readonly origin: number;
  readonly days: number;
  readonly hoursPerDay: number;
  private readonly iv: DayIntervals[];
  private readonly cum: Float64Array;

  constructor(def: CalendarDef, fromDay: number, days: number) {
    this.origin = fromDay;
    this.days = days;
    this.hoursPerDay = def.hoursPerDay > 0 ? def.hoursPerDay : 8;
    this.iv = new Array(days);
    this.cum = new Float64Array(days + 1);
    const holidaysByYear = new Map<number, Map<string, string>>();
    const satByYear = new Map<number, Set<string>>();
    // Исключения раскладываются по дням горизонта один раз; позднее в списке — главнее
    const excAt: Array<CalendarException | undefined> = new Array(days);
    for (const e of def.exceptions) {
      const a = Math.max(dayNumber(e.from) - fromDay, 0);
      const b = Math.min(dayNumber(e.to) - fromDay, days - 1);
      for (let k = a; k <= b; k++) excAt[k] = e;
    }
    const seas = def.seasonal.map((s) => ({ a: s.from, b: s.to }));
    const firstShift = def.week.find((x) => x.length) ?? def.rotation?.day ?? [[480, 1020]];
    const rotAnchor = def.rotation ? dayNumber(def.rotation.anchor) : 0;
    let total = 0;
    for (let i = 0; i < days; i++) {
      const d = fromDay + i;
      const ds = dayString(d);
      let day: DayIntervals = def.week[weekday(d)] ?? [];
      if (def.rotation) {
        const cycle = def.rotation.on + def.rotation.off;
        const pos = (((d - rotAnchor) % cycle) + cycle) % cycle;
        day = pos < def.rotation.on ? def.rotation.day : [];
      }
      if (def.holidays === 'kz') {
        const y = Number(ds.slice(0, 4));
        let hol = holidaysByYear.get(y);
        if (!hol) {
          hol = kzHolidays(y);
          holidaysByYear.set(y, hol);
        }
        let sat = satByYear.get(y);
        if (!sat) {
          sat = kzWorkingSaturdays(y);
          satByYear.set(y, sat);
        }
        if (hol.has(ds)) day = [];
        else if (sat.has(ds)) day = def.week[0]?.length ? def.week[0] : firstShift;
      }
      const md = ds.slice(5);
      for (const s of seas) {
        const inside = s.a <= s.b ? md >= s.a && md <= s.b : md >= s.a || md <= s.b;
        if (inside) day = [];
      }
      const e = excAt[i];
      if (e) {
        day = e.work ? (e.day?.length ? e.day : def.week[weekday(d)]?.length ? day : firstShift) : [];
        // Рабочий день по исключению при выходном недели: берём часы первого рабочего дня
        if (e.work && !day.length) day = firstShift;
      }
      this.iv[i] = day;
      this.cum[i] = total;
      for (const [a, b] of day) total += b - a;
    }
    this.cum[days] = total;
  }

  get start(): number {
    return this.origin * DAY;
  }
  get end(): number {
    return (this.origin + this.days) * DAY;
  }

  private dayIndex(t: number): number {
    return Math.floor(t / DAY) - this.origin;
  }

  /** Рабочих минут от начала горизонта до момента t */
  workAt(t: number): number {
    const i = this.dayIndex(t);
    if (i < 0) return 0;
    if (i >= this.days) return this.cum[this.days]!;
    const m = t - (this.origin + i) * DAY;
    let w = this.cum[i]!;
    for (const [a, b] of this.iv[i]!) {
      if (m <= a) break;
      w += Math.min(m, b) - a;
    }
    return w;
  }

  /** Первый день i, у которого после начала больше w рабочих минут (cum[i+1] > w) */
  private searchAfter(w: number): number {
    let lo = 0;
    let hi = this.days - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (this.cum[mid + 1]! > w) hi = mid;
      else lo = mid + 1;
    }
    return lo;
  }

  /** Самое позднее начало w-й рабочей минуты: момент, с которого сразу идёт работа */
  startAt(w: number): number {
    if (w < 0) return this.start;
    if (w >= this.cum[this.days]!) return this.end;
    const i = this.searchAfter(w);
    let rem = w - this.cum[i]!;
    for (const [a, b] of this.iv[i]!) {
      if (rem < b - a) return (this.origin + i) * DAY + a + rem;
      rem -= b - a;
    }
    return (this.origin + i + 1) * DAY;
  }

  /** Самый ранний момент, когда набрано w рабочих минут: конец рабочей минуты */
  finishAt(w: number): number {
    if (w <= 0) return this.firstWorkStart();
    if (w > this.cum[this.days]!) return this.end;
    const i = this.searchAfter(w - 1);
    let rem = w - this.cum[i]!;
    for (const [a, b] of this.iv[i]!) {
      if (rem <= b - a) return (this.origin + i) * DAY + a + rem;
      rem -= b - a;
    }
    return (this.origin + i + 1) * DAY;
  }

  private firstWorkStart(): number {
    return this.startAt(0);
  }

  /** Рабочий ли день (для шкалы диаграммы) */
  isWorkDay(dayNo: number): boolean {
    const i = dayNo - this.origin;
    if (i < 0 || i >= this.days) return false;
    return this.iv[i]!.length > 0;
  }

  /** Рабочие промежутки дня (для выгрузки календаря в файлы обмена) */
  dayIntervals(dayNo: number): DayIntervals {
    const i = dayNo - this.origin;
    if (i < 0 || i >= this.days) return [];
    return this.iv[i]!;
  }

  /** Рабочих минут в днях */
  minutesPerDay(): number {
    return this.hoursPerDay * 60;
  }
}

/** Рабочих часов в дне по определению календаря (среднее по рабочим дням недели или смене вахты) */
export function hoursPerDayOf(def: Pick<CalendarDef, 'week' | 'rotation'>): number {
  const days = def.rotation ? [def.rotation.day] : def.week.filter((d) => d.length);
  if (!days.length) return 8;
  const mins = days.reduce((s, d) => s + d.reduce((x, [a, b]) => x + b - a, 0), 0) / days.length;
  return Math.round((mins / 60) * 100) / 100;
}
